import { useMemo, useState } from 'react';
import type { ClauseTreeNode, ResponseListItem } from '@grc/types';

interface ClauseStats {
  total: number;
  done: number;
}

function collectStats(
  node: ClauseTreeNode,
  byClause: Map<string, ClauseStats>,
  rollup: Map<string, ClauseStats>,
): ClauseStats {
  const own = byClause.get(node.id) ?? { total: 0, done: 0 };
  const sum = { ...own };
  for (const child of node.children) {
    const childStats = collectStats(child, byClause, rollup);
    sum.total += childStats.total;
    sum.done += childStats.done;
  }
  rollup.set(node.id, sum);
  return sum;
}

export function ClauseTree({
  clauses,
  responses,
  selectedClauseId,
  onSelect,
}: {
  clauses: ClauseTreeNode[];
  responses: ResponseListItem[];
  selectedClauseId: string | null;
  onSelect: (clauseId: string | null) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const rollup = useMemo(() => {
    const byClause = new Map<string, ClauseStats>();
    for (const r of responses) {
      const entry = byClause.get(r.clauseId) ?? { total: 0, done: 0 };
      entry.total += 1;
      if (['finalised', 'assessed', 'locked'].includes(r.status)) entry.done += 1;
      byClause.set(r.clauseId, entry);
    }
    const out = new Map<string, ClauseStats>();
    for (const root of clauses) collectStats(root, byClause, out);
    return out;
  }, [clauses, responses]);

  const overall = responses.length;
  const overallDone = responses.filter((r) =>
    ['finalised', 'assessed', 'locked'].includes(r.status),
  ).length;

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (node: ClauseTreeNode) => {
    const stats = rollup.get(node.id) ?? { total: 0, done: 0 };
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(node.id);
    return (
      <li key={node.id}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {hasChildren ? (
            <button
              className="clause-caret"
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} clause ${node.clauseNumber}`}
              aria-expanded={!isCollapsed}
              onClick={() => toggle(node.id)}
            >
              {isCollapsed ? '▸' : '▾'}
            </button>
          ) : (
            <span className="clause-caret" aria-hidden="true" />
          )}
          <button
            className={`clause-row ${selectedClauseId === node.id ? 'selected' : ''}`}
            onClick={() => onSelect(node.id)}
            title={`${node.clauseNumber} ${node.title}`}
          >
            <span className="num">{node.clauseNumber}</span>
            <span className="title">{node.title}</span>
            {stats.total > 0 && (
              <span className={`frac ${stats.done === stats.total ? 'done' : ''}`}>
                {stats.done}/{stats.total}
              </span>
            )}
          </button>
        </div>
        {hasChildren && !isCollapsed && <ul>{node.children.map(renderNode)}</ul>}
      </li>
    );
  };

  return (
    <nav className="clause-tree" aria-label="Clause navigation">
      <ul>
        <li>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="clause-caret" aria-hidden="true" />
            <button
              className={`clause-row ${selectedClauseId === null ? 'selected' : ''}`}
              onClick={() => onSelect(null)}
            >
              <span className="title">All clauses</span>
              <span className={`frac ${overallDone === overall && overall > 0 ? 'done' : ''}`}>
                {overallDone}/{overall}
              </span>
            </button>
          </div>
        </li>
        {clauses.map(renderNode)}
      </ul>
    </nav>
  );
}
